import { create } from 'zustand';
import i18next from 'i18next';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  duration?: number; // en ms, 0 = permanent
  dismissible?: boolean;
  action?: { label: string; onClick: () => void }; // Bouton d'action (ferme la notification)
}

interface NotificationState {
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id'>) => string;
  removeNotification: (id: string) => void;
  clearAll: () => void;
}

// Générateur d'ID unique
let notificationId = 0;
const generateId = () => `notification-${++notificationId}`;

export const useNotificationStore = create<NotificationState>((set) => ({
  notifications: [],

  addNotification: (notification) => {
    const id = generateId();
    const newNotification: Notification = {
      id,
      duration: 5000, // 5 secondes par défaut
      dismissible: true,
      ...notification,
    };

    set((state) => ({
      notifications: [...state.notifications, newNotification],
    }));

    // Auto-dismiss si duration > 0
    if (newNotification.duration && newNotification.duration > 0) {
      setTimeout(() => {
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
        }));
      }, newNotification.duration);
    }

    return id;
  },

  removeNotification: (id) => {
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    }));
  },

  clearAll: () => {
    set({ notifications: [] });
  },
}));

// Helper pour afficher une erreur de rate limiting
export function showRateLimitError(retryAfter?: number, backendMessage?: string) {
  // Utiliser le message du backend s'il est disponible, sinon générer un message basé sur retryAfter
  let message: string;

  if (backendMessage) {
    message = backendMessage;
  } else {
    const minutes = retryAfter ? Math.ceil(retryAfter / 60) : 5;
    message = i18next.t('notification.rateLimitMessage', { minutes, ns: 'common' });
  }

  useNotificationStore.getState().addNotification({
    type: 'error',
    title: i18next.t('notification.rateLimitTitle', { ns: 'common' }),
    message,
    duration: 10000, // 10 secondes
    dismissible: true,
  });
}
